"use strict";
// packages/shared/src/risk.ts
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateConsequence = calculateConsequence;
exports.calculateRiskScore = calculateRiskScore;
exports.getRiskRating = getRiskRating;
exports.calculateRisk = calculateRisk;
/**
 * Numerical weights for likelihood levels based on AS/NZS ISO 31000
 */
const LIKELIHOOD_WEIGHTS = {
    rare: 1, // 5% chance
    unlikely: 2, // 20% chance
    possible: 3, // 40% chance
    likely: 4, // 60% chance
    almost_certain: 5, // 95% chance
};
/**
 * Numerical weights for severity levels based on AS/NZS ISO 31000
 */
const SEVERITY_WEIGHTS = {
    insignificant: 1, // No injuries, negligible impact
    minor: 2, // First aid treatment, minor damage
    moderate: 3, // Disabling injury, significant disruption
    major: 4, // Fatality, extensive loss
    catastrophic: 5, // Multiple fatalities, total loss
};
/**
 * Risk matrix based on AS/NZS ISO 31000 and HACCP standards
 * Rows: Likelihood (1-5), Columns: Severity (1-5)
 * Returns consequence level based on score:
 * - Low (1-4): Acceptable, no further action needed
 * - Moderate (5-9): Adequate, may need further analysis
 * - High (10-16): Tolerable, must be reviewed in timely manner
 * - Extreme (17-25): Unacceptable, immediate action required
 */
const RISK_MATRIX = {
    1: { 1: 'low', 2: 'low', 3: 'low', 4: 'low', 5: 'moderate' },
    2: { 1: 'low', 2: 'low', 3: 'moderate', 4: 'moderate', 5: 'moderate' },
    3: { 1: 'low', 2: 'moderate', 3: 'moderate', 4: 'high', 5: 'high' },
    4: { 1: 'low', 2: 'moderate', 3: 'high', 4: 'high', 5: 'extreme' },
    5: { 1: 'moderate', 2: 'moderate', 3: 'high', 4: 'extreme', 5: 'extreme' },
};
/**
 * Calculates the consequence level based on likelihood and severity
 * following AS/NZS ISO 31000 risk matrix
 *
 * @param likelihood - Likelihood of the hazard occurring
 * @param severity - Severity/consequence if the hazard occurs
 * @returns Consequence level (low, moderate, high, extreme)
 */
function calculateConsequence(likelihood, severity) {
    const likelihoodWeight = LIKELIHOOD_WEIGHTS[likelihood];
    const severityWeight = SEVERITY_WEIGHTS[severity];
    return RISK_MATRIX[likelihoodWeight][severityWeight];
}
/**
 * Calculates the numerical risk score based on likelihood and severity
 * Score = Likelihood Weight × Severity Weight
 * Range: 1 (lowest risk) to 25 (highest risk)
 *
 * @param likelihood - Likelihood of the hazard occurring
 * @param severity - Severity/consequence if the hazard occurs
 * @returns Risk score (1-25)
 */
function calculateRiskScore(likelihood, severity) {
    const likelihoodWeight = LIKELIHOOD_WEIGHTS[likelihood];
    const severityWeight = SEVERITY_WEIGHTS[severity];
    return likelihoodWeight * severityWeight;
}
/**
 * Gets the risk rating based on the risk score
 *
 * @param score - Risk score (1-25)
 * @returns Risk rating classification
 */
function getRiskRating(score) {
    if (score <= 3)
        return 'low';
    if (score <= 8)
        return 'moderate';
    if (score <= 15)
        return 'high';
    return 'extreme';
}
/**
 * Calculates both consequence and risk score in a single call
 *
 * @param likelihood - Likelihood of the hazard occurring
 * @param severity - Severity/consequence if the hazard occurs
 * @returns Object containing consequence and score
 */
function calculateRisk(likelihood, severity) {
    return {
        consequence: calculateConsequence(likelihood, severity),
        score: calculateRiskScore(likelihood, severity),
    };
}
